import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import FloatingNavigation from "../components/FloatingNavigation";
import {
  FaPlus,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaUsers,
  FaChartLine,
  FaDownload,
  FaSearch,
  FaFilter,
  FaMapMarkerAlt,
  FaBuilding,
  FaTimes
} from "react-icons/fa";

export default function SiteManagement() {
  const { token } = useAuth();
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteStats, setSiteStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newSite, setNewSite] = useState({
    siteName: "",
    displayName: "",
    description: "",
  });

  useEffect(() => {
    if (token) {
      fetchSites();
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSites = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSites(response.data.sites || []);
    } catch (error) {
      console.error("Error fetching sites:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchSiteStats = async (siteId) => {
    setLoading(true);
    try {
      // Fetch site statistics (you may need to create this endpoint)
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/${siteId}/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSiteStats(response.data);
    } catch (error) {
      console.error("Error fetching site stats:", error);
      setSiteStats({});
    } finally {
      setLoading(false);
    }
  };

  const handleSiteSelect = (site) => {
    setSelectedSite(site);
    fetchSiteStats(site._id);
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/create`,
        newSite,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNewSite({ siteName: "", displayName: "", description: "" });
      setShowAddForm(false);
      fetchSites();
      alert("Site created successfully!");
    } catch (error) {
      console.error("Error creating site:", error);
      alert(
        "Error creating site: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleDeleteSite = async (siteId) => {
    // First confirmation
    if (
      !window.confirm(
        "Are you sure you want to delete this site? This action cannot be undone."
      )
    ) {
      return;
    }

    // Ask for security code
    const securityCode = window.prompt(
      "This is a destructive action. Please enter the security code to confirm site deletion:"
    );

    if (!securityCode) {
      alert("Site deletion cancelled.");
      return;
    }

    if (securityCode !== "Vrarchives@64") {
      alert("❌ Invalid security code. Site deletion cancelled.");
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/${siteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchSites();
      setSelectedSite(null);
      alert("Site deleted successfully!");
    } catch (error) {
      console.error("Error deleting site:", error);
      alert(
        "Error deleting site: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleToggleSiteStatus = async (siteId, currentStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/${siteId}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSites();
      if (selectedSite && selectedSite._id === siteId) {
        setSelectedSite((prev) => ({ ...prev, isActive: !currentStatus }));
      }
    } catch (error) {
      console.error("Error updating site status:", error);
      alert(
        "Error updating site status: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const getSiteUsers = (siteId) => {
    return users.filter((user) => user.assignedSite?._id === siteId);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
    }).format(amount || 0);
  };

  const filteredSites = sites.filter((site) => {
    const matchesSearch = 
      site.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.siteName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === "all" || 
      (statusFilter === "active" && site.isActive) ||
      (statusFilter === "inactive" && !site.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const exportToCSV = () => {
    const csvData = sites.map(site => ({
      "Site Name": site.siteName,
      "Display Name": site.displayName,
      "Description": site.description,
      "Status": site.isActive ? "Active" : "Inactive",
      "Team Members": getSiteUsers(site._id).length,
      "Created": new Date(site.createdAt).toLocaleDateString()
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map(row => Object.values(row).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sites-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Site Management</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage your business locations and monitor site performance
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaPlus className="mr-2" />
                {showAddForm ? "Cancel" : "Add Site"}
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100">
                <FaBuilding className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Sites</p>
                <p className="text-2xl font-semibold text-gray-900">{sites.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-100">
                <FaEye className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Sites</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {sites.filter(site => site.isActive).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-yellow-100">
                <FaEyeSlash className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Inactive Sites</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {sites.filter(site => !site.isActive).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-100">
                <FaUsers className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Team Members</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {users.filter(user => user.role === "Team").length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Add Site Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Site</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddSite} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Name (ID)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., colombo-main"
                    value={newSite.siteName}
                    onChange={(e) =>
                      setNewSite((prev) => ({ ...prev, siteName: e.target.value }))
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Colombo Main Store"
                    value={newSite.displayName}
                    onChange={(e) =>
                      setNewSite((prev) => ({ ...prev, displayName: e.target.value }))
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Site description..."
                  value={newSite.description}
                  onChange={(e) =>
                    setNewSite((prev) => ({ ...prev, description: e.target.value }))
                  }
                  rows="3"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create Site
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Sites
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status Filter
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="all">All Sites</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                }}
                className="w-full px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaFilter className="inline mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sites List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Sites ({filteredSites.length})
                </h3>
              </div>
              <div className="p-6">
                {filteredSites.length === 0 ? (
                  <div className="text-center py-12">
                    <FaMapMarkerAlt className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No sites found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== "all" 
                        ? "Try adjusting your search or filters" 
                        : "Get started by creating your first site"}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {filteredSites.map((site) => (
                      <div
                        key={site._id}
                        className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                          selectedSite?._id === site._id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        } ${!site.isActive ? "opacity-75" : ""}`}
                        onClick={() => handleSiteSelect(site)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className={`w-3 h-3 rounded-full ${site.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <h4 className="text-lg font-semibold text-gray-900">
                                {site.displayName}
                              </h4>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  site.isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {site.isActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">ID: {site.siteName}</p>
                            <p className="text-sm text-gray-600 mt-2">{site.description}</p>
                            <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                              <span className="flex items-center">
                                <FaUsers className="mr-1" />
                                {getSiteUsers(site._id).length} members
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleSiteStatus(site._id, site.isActive);
                              }}
                              className={`p-2 rounded-md transition-colors ${
                                site.isActive
                                  ? "text-green-600 hover:bg-green-100"
                                  : "text-red-600 hover:bg-red-100"
                              }`}
                              title={site.isActive ? "Deactivate Site" : "Activate Site"}
                            >
                              {site.isActive ? <FaEye /> : <FaEyeSlash />}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSite(site._id);
                              }}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                              title="Delete Site"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Site Details */}
          <div className="lg:col-span-1">
            {selectedSite ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedSite.displayName}
                  </h3>
                  <p className="text-sm text-gray-500">Site Details</p>
                </div>
                
                {/* Site Statistics */}
                <div className="p-6 border-b border-gray-200">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-500">Total Sales</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {formatCurrency(siteStats.totalSales)}
                            </p>
                          </div>
                          <FaChartLine className="h-8 w-8 text-blue-500" />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500">Today's Sales</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {formatCurrency(siteStats.todaySales)}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500">Total Tickets</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {siteStats.totalTickets || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Team Members */}
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Team Members ({getSiteUsers(selectedSite._id).length})
                  </h4>
                  <div className="space-y-3">
                    {getSiteUsers(selectedSite._id).length > 0 ? (
                      getSiteUsers(selectedSite._id).map((user) => (
                        <div
                          key={user._id}
                          className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {user.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {user.name}
                            </p>
                            <p className="text-xs text-gray-500">{user.role}</p>
                            {user.email && (
                              <p className="text-xs text-gray-400 truncate">{user.email}</p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6">
                        <FaUsers className="mx-auto h-8 w-8 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">
                          No team members assigned
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-center py-12">
                  <FaMapMarkerAlt className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No site selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select a site from the list to view details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <FloatingNavigation />
    </div>
  );
}
